import { TranslateService } from '@ngx-translate/core';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { RepositoryService } from '../../../smoothr-web-app-core/services/repository/repository.service';
import RepositoryDirective from '../../../smoothr-web-app-core/directives/repository-directive';
import { environment } from '../../../environments/environment';
import { PreorderType } from '../../../smoothr-web-app-core/enums/PreorderType';
import { isVenueOpen, sleep, venueAcceptsOrders } from '../../../smoothr-web-app-core/utils/utils';
import Venue from '../../../smoothr-web-app-core/models/Venue';
import Address from '../../../smoothr-web-app-core/models/Address';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { BehaviorSubject } from 'rxjs';
import { MenuPage } from '../menu/menu.page';
import { HomePage } from '../home/home.page';
import { OrderType } from 'src/smoothr-web-app-core/enums/OrderType';
import { SelectLanguageModalComponent } from 'src/app/components/select-language-modal/select-language-modal.component';
import { ScanQrModal } from 'src/app/components/scan-qr/scan-qr.component';
import { Api } from 'src/smoothr-web-app-core/api/api';
import { GeolocationService } from 'src/app/services/geolocation.service';
import moment from 'moment';
import Swiper from 'swiper';

enum Language {
	GreatBritain = 'en',
	German = 'de',
}

interface pinPosition {
	sector: string;
	posX: number;
	posY: number;
}

@Component({
	selector: 'app-select-venue',
	templateUrl: './select-venue.page.html',
	styleUrls: ['./select-venue.page.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectVenuePage extends RepositoryDirective implements OnInit {
	@ViewChild('swiper', { static: false }) swiperElement: ElementRef;
	@Input() name!: string;

	swiper: Swiper;

	static url = 'select-venue';
	environment = environment;
	languageEnum = Language;
	displayStadiumPosition = false;

	stadiumPins: pinPosition[] = [
		{
			sector: 'Block 3',
			posX: 222,
			posY: 29,
		},
		{
			sector: 'Block 9',
			posX: 340,
			posY: 29,
		},
		{
			sector: 'Block 15',
			posX: 410,
			posY: 96,
		},
		{
			sector: 'Block 18',
			posX: 380,
			posY: 146,
		},
		{
			sector: 'Block 31',
			posX: 224,
			posY: 237,
		},
		{
			sector: 'Block 38',
			posX: 185,
			posY: 155,
		},
		{
			sector: 'Block 39',
			posX: 150,
			posY: 170,
		},
	];

	loading = false;
	preorderTypes = [PreorderType.TAKE_AWAY];
	active: any = {};
	loading$ = new BehaviorSubject(false);
	public relevantVenues: Venue[] = [];
	isVenueOpen = isVenueOpen;

	acceptsTakeaway = false;
	firstTakeAwayTime: string = null;

	// Stadium location view properties
	selectedVenue: Venue | null = null;
	selectedVenueIndex: number = 0;
	latitude: number | undefined;
	longitude: number | undefined;
	errorMessage: string = '';

	constructor(
		protected repository: RepositoryService,
		public translate: TranslateService,
		private snackbarCtrl: MatSnackBar,
		private route: ActivatedRoute,
		private router: Router,
		private modalCtrl: ModalController,
		private cdr: ChangeDetectorRef,
		private geolocationService: GeolocationService
	) {
		super(repository);
		// get user location
		this.getLocation();
		this.loading$.next(false);
	}

	static navigate(router: Router, param?: string) {
		return router.navigate([SelectVenuePage.url], { queryParams: { name: param } });
	}

	ngOnInit() {
		super.ngOnInit();
		this.route.params.subscribe((params: any) => {
			if (params && params?.shortId) {
				this.loadSelectedVenue(params?.shortId);
			} else {
				this.locateAndShowStores(PreorderType.TAKE_AWAY);
			}
		});
	}

	async loadSelectedVenue(venueId: string) {
		try {
			console.log(venueId);

			this.loading$.next(true);
			const venues = await this.repository.getAllVenues(environment.customerGroup);
			if (venues && venues.length > 0) {
				this.relevantVenues = venues;
				// Set the first venue as selected by default
				this.selectedVenue = venues[0];
				this.selectedVenueIndex = 0;
			}
			console.log(this.relevantVenues);
		} catch (error) {
			console.log('loadSelectedVenue has error!!!!');
			await this.locateAndShowStores(PreorderType.INSIDE);
		}
		this.loading$.next(false);
		this.cdr.markForCheck();
	}

	ionViewDidEnter() {
		if (history.state.navigationId === 1) {
			this.repository.onOrderChange(null);
		}
		this.repository.verifiedOrder.emit(null);
	}

	async locateAndShowStores(_preorderType: PreorderType) {
		this.loading$.next(true);
		const venues = await this.repository.getAllVenues(environment.customerGroup);
		if (venues.length > 0) {
			this.relevantVenues = venues.filter(ven => venueAcceptsOrders(ven, _preorderType));
			// Set the first venue as selected by default
			this.selectedVenue = this.relevantVenues[0];
			console.log(this.selectedVenue);
			this.selectedVenueIndex = 0;

			// Load article categories for each venue and calculate distance from user location to each venue
			this.relevantVenues.forEach(async venue => {
				const categories = (await Api.getCategoriesWithArticles(venue._id, true)).data;
				venue = Object.assign(venue, { articleCategories: categories });
				// hardcoded user location for testing
				this.latitude = 50.06861;
				this.longitude = 8.64509;
				// const distance = this.geolocationService.calculateDistance(50.07071, 8.65171, 50.06861, 8.64509);
				const distance = this.geolocationService.calculateDistance(
					this.latitude,
					this.longitude,
					venue.location.coordinates[1],
					venue.location.coordinates[0]
				);
				venue = Object.assign(venue, { location: { ...venue.location, distanceFromUser: distance.toFixed(0) } });
				console.log('Distance to venue', venue.name, ':', distance.toFixed(0), 'meters');
			});
			this.relevantVenues = this.relevantVenues.sort((a, b) => {
				return a.externalLocationId.localeCompare(b.externalLocationId, undefined, { numeric: true });
			});
			this.stadiumPins = this.stadiumPins.filter(p => this.relevantVenues.find(v => v.externalLocationId.includes(p.sector)));
		}
		this.loading$.next(false);
		this.cdr.detectChanges();
	}

	async loadVenueAndCreateOrder(venue: Venue, address: Address, preorderType: PreorderType) {
		const loadedVenue = await this.repository.getVenue(venue._id);
		this.repository.createOrder(loadedVenue, address, OrderType.PREORDER, preorderType);
		this.loading$.next(false);
	}

	loadVenues() {
		for (const preorderType of this.preorderTypes) {
			this.active[preorderType] = false;
		}
		new Promise<void>(async (resolve, reject) => {
			try {
				const venues = await this.repository.getAllVenues(environment.customerGroup);
				for (const venue of venues) {
					for (const preorderType of this.preorderTypes) {
						this.active[preorderType] = this.active[preorderType] || venueAcceptsOrders(venue, preorderType);
					}
				}
				resolve();
			} catch (e) {
				reject(e);
			}
		}).finally(() => this.loading$.next(false));
	}

	async goToMenu(type: PreorderType) {
		this.locateAndShowStores(type);
	}

	async goHome() {
		HomePage.navigate(this.router, undefined, this.name);
	}

	changeLang(language: string) {
		this.translate.setDefaultLang(language);
		this.translate.use(language);
	}

	async selectLanguageModal() {
		await SelectLanguageModalComponent.show(this.modalCtrl);
	}

	goBack() {
		// this.navigationService.navigateToUrlWithParams(NAVIGATIONURLS.home(), null);
	}

	isVenueOpenFunc(venue: Venue) {
		return this.isVenueOpen(venue) ? this.translate.instant('venue_suggestion.open') : this.translate.instant('venue_suggestion.close');
	}

	async openInfoPage(venue: Venue) {
		console.log('Venue info:', venue);
	}

	accepts(type: PreorderType): boolean {
		switch (type) {
			case PreorderType.TAKE_AWAY:
				return this.acceptsTakeaway;
			default:
				return false;
		}
	}

	async selectVenue(venue: Venue, type: PreorderType = PreorderType.TAKE_AWAY) {
		// Disable if venue is in panic mode
		if (venue.panicEndAt && venue.panicEndAt.isAfter(moment())) {
			this.snackbarCtrl.open(this.translate.instant('home_page.venue_in_panic'), null, {
				duration: 5000,
			});
			return;
		}
		this.loading$.next(true);
		const loadedVenue = await this.repository.getVenue(venue._id);
		console.log(loadedVenue);
		await sleep(100);
		if (!loadedVenue) {
			this.loading$.next(false);
			this.snackbarCtrl.open(this.translate.instant('home_page.venue_closed'), null, {
				duration: 5000,
			});
			return;
		}
		this.repository.createOrder(loadedVenue, null, OrderType.PREORDER, type);
		sleep(100);

		if (type === PreorderType.TAKE_AWAY) {
			this.loading$.next(false);
			await MenuPage.navigate(this.router, this.name);
		}
	}

	/**
	 * Select a venue from the stadium location view
	 */
	selectStadiumVenue(venue: Venue, index: number) {
		this.selectedVenue = venue;
		this.selectedVenueIndex = index;
	}

	showStadiumLocation() {
		this.displayStadiumPosition = true;
		// Set first venue as selected when switching to stadium view
		if (this.relevantVenues.length > 0 && !this.selectedVenue) {
			this.selectedVenue = this.relevantVenues[0];
			this.selectedVenueIndex = 0;
		}
	}

	showList() {
		this.displayStadiumPosition = false;
	}

	getLocationMenuIcon() {
		if (!this.displayStadiumPosition) {
			return '../../../assets/eintracht/location-menu.svg';
		} else {
			if (!!this.name) {
				return '../../../assets/eintracht/location-menu-active-eintracht.svg';
			} else {
				return '../../../assets/eintracht/location-menu-active.svg';
			}
		}
	}

	async scanQrCode() {
		await ScanQrModal.show(this.modalCtrl, this.name);
	}

	getKioskNumber(externalLocationId: string): string {
		if (!externalLocationId) {
			return '';
		}
		const parts = externalLocationId.split(',');
		return parts.length > 1 ? parts[0] : externalLocationId;
	}

	getBlockNumber(externalLocationId: string): string {
		if (!externalLocationId) {
			return '';
		}
		const parts = externalLocationId.split(',');
		return parts.length > 1 ? parts[1] : externalLocationId;
	}

	getLocation(): void {
		this.geolocationService.getPosition().subscribe(
			(position: GeolocationPosition) => {
				this.latitude = position.coords.latitude;
				this.longitude = position.coords.longitude;
				this.errorMessage = '';
			},
			(error: GeolocationPositionError | string) => {
				if (typeof error === 'string') {
					this.errorMessage = error;
				} else {
					switch (error.code) {
						case error.PERMISSION_DENIED:
							this.errorMessage = 'User denied the request for Geolocation.';
							break;
						case error.POSITION_UNAVAILABLE:
							this.errorMessage = 'Location information is unavailable.';
							break;
						case error.TIMEOUT:
							this.errorMessage = 'The request to get user location timed out.';
							break;
						default:
							this.errorMessage = 'An unknown error occurred.';
							break;
					}
				}
				console.error(error);
			}
		);
	}

	onSwiperInit(e: any) {
		console.log('onSwiperInit', e);
		setTimeout(() => {
			this.swiper = this.swiperElement.nativeElement.swiper;
			this.swiper.on('realIndexChange', e => {
				if (isNaN(e.activeIndex)) return;
				this.selectedVenue = this.relevantVenues[e.activeIndex];
				this.cdr.detectChanges();
			});
		});
	}

	setFocusOnVenue(pin: pinPosition) {
		const idx = this.relevantVenues.findIndex(v => v.externalLocationId.includes(pin.sector));
		this.selectedVenue = this.relevantVenues[idx];
		this.swiper.slideTo(idx);
	}
}
